import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';
import { Link as ReactRouterLink, useSearchParams } from 'react-router-dom';
import { useNetwork, ApiError } from '../../utils/network';
import { AuthShell, AuthCard } from '../../components/AuthShell';
import { PT } from '../../components/portalUi';

/* /account/2fa-reset/cancel?token=… — the one-click cancel from the "a two-factor
 * reset was requested" email. The page shows what it is about to do and cancels on
 * the click, not on the visit: a mail scanner opening the link must not spend the
 * token. The token is single-use; the server's answer is the same words whether it
 * was already used, expired or never valid, and says when it worked.
 *
 * Whatever the answer, the page ends with the one thing that matters if this was
 * not the member: reset the password. "Already used or expired" can mean the reset
 * already went through, which is exactly when that advice counts most. */
export const TwoFactorResetCancel = () => {
  const { post } = useNetwork();
  const [params] = useSearchParams();
  // read once, then taken out of the address bar: it must not sit in history,
  // bookmarks or anything that records the URL
  const [token] = useState(() => (params.get('token') || '').trim());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ cancelled: boolean; message: string } | null>(null);

  useEffect(() => {
    if (token && window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [token]);

  const cancel = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await post('/auth/two-factor-reset/cancel', { token }, false);
      setResult({ cancelled: Boolean(res?.cancelled), message: res?.message || 'Done.' });
    } catch (err) {
      const e = err as ApiError;
      setError(e?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <AuthCard>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'center' }}>
          <Typography component="h1" variant="h5">
            Cancel the two-factor reset
          </Typography>

          {!token ? (
            <Typography sx={{ mt: 2, color: PT.textMuted, lineHeight: 1.5 }}>
              This link is missing its token. Open the link from the email again, or sign in with your
              authenticator or a recovery code — that cancels the request too.
            </Typography>
          ) : result ? (
            <Typography role="status" sx={{ mt: 2, fontWeight: 700, color: PT.text, lineHeight: 1.5 }}>
              {result.message}
            </Typography>
          ) : (
            <>
              <Typography sx={{ mt: 2, color: PT.textMuted, lineHeight: 1.5 }}>
                Somebody asked for the two-factor authentication on your account to be reset. If that was
                not you — or you have found your authenticator — cancel it here. Nothing has changed yet;
                cancelling stops it.
              </Typography>
              {error && (
                <Typography role="alert" sx={{ mt: 2, color: '#ff8a80', lineHeight: 1.5 }}>
                  {error}
                </Typography>
              )}
              <Button
                variant="contained"
                fullWidth
                onClick={cancel}
                disabled={busy}
                sx={{ mt: 3 }}
                startIcon={busy ? <CircularProgress size={18} /> : undefined}
              >
                {busy ? 'Cancelling…' : 'Cancel the reset request'}
              </Button>
            </>
          )}

          <Typography sx={{ mt: 2.5, color: PT.textMuted, lineHeight: 1.5, fontSize: 14 }}>
            If you did not ask for this reset, somebody else has your password: reset it now, and contact
            support if anything looks wrong.
          </Typography>

          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Link component={ReactRouterLink} to="/set-password" variant="body2">
              Reset your password
            </Link>
            <Link component={ReactRouterLink} to="/login" variant="body2">
              Sign in
            </Link>
          </Box>
        </Box>
      </AuthCard>
    </AuthShell>
  );
};
